import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { startCase, update } from 'lodash';
import { addISOWeekYears } from 'date-fns';
import ApplicationBidModal from './ApplicationBidModal';
import { rateTypeSelect } from '../../../utils/forms/preconfiguredInputs';
import KeyValPair from '../../theme/data/KeyValuePair';
import user from '../../../store/reducers/user';
import formatters from '../../../utils/forms/formatters';
import {
	updateBid,
	updateOrCreateApplication,
	pullApplication,
	pullApplicationBids,
	prepareApplicationFormFlow,
} from '../../../store/calls/applicationData';
import ConfirmModal from '../../theme/modals/ConfirmModal';
import { toggleGlobalLoader } from '../../../store/actions/session';
import { store } from '../../../store';
import * as actions from '../../../store/actions';
import {
	addSuccessAlert,
	addErrorAlert,
} from '../../../store/calls/alertLoadingDispatch';

function ApproveOrRejectBidModal({
	is_approved,
	application_id,
	bidData,
	onCancel,
}) {
	const dispatchApproval = () => {
		toggleGlobalLoader(true);
		// When approving, also clear is_bid_decline flag
		const updateData = is_approved 
			? { is_approved, is_bid_decline: false }
			: { is_approved };
		
		updateBid(application_id, bidData, updateData)
			.then(() => {
				toggleGlobalLoader(false);
				addSuccessAlert('Bid was updated successfully.');
				onCancel();
			})
			.catch(() => {
				toggleGlobalLoader(false);
				addErrorAlert('There was an error updating the bid.');
			});
	};

	return (
		<ConfirmModal
			visible
			type={is_approved ? 'info' : 'warning'}
			prompt={is_approved ? 'Approve this bid?' : 'Reject this bid?'}
			onAccept={() => {
				dispatchApproval();
			}}
			onCancel={onCancel}
			data-testid="approve-reject-bid-modal"
		/>
	);
}

function AcceptBidModal({ application_id, bidData, onCancel }) {
	const dispatchAcceptance = () => {
		toggleGlobalLoader(true);
		updateOrCreateApplication({ id: application_id, loan_estimate: bidData.id })
			.then(() => Promise.all([
				pullApplication(application_id),
				pullApplicationBids(application_id),
			]))
			.then(() => {
				toggleGlobalLoader(false);
				addSuccessAlert('Bid selected.');
				onCancel();
			})
			.catch(() => {
				toggleGlobalLoader(false);
				addErrorAlert('There was an error.');
			});
	};

	return (
		<ConfirmModal
			visible
			type="info"
			prompt="Are you sure you want to accept this bid?"
			onAccept={() => {
				dispatchAcceptance();
			}}
			onCancel={onCancel}
			data-testid="accept-bid-modal"
		/>
	);
}

function RejectBidModal({ application_id, bidData, onCancel }) {
	const dispatchRejection = () => {
		toggleGlobalLoader(true);
		updateBid(application_id, bidData, { is_bid_decline: true })
			.then(() => {
				toggleGlobalLoader(false);
				addSuccessAlert('Bid rejected and hidden.');
				onCancel();
			})
			.catch(() => {
				toggleGlobalLoader(false);
				addErrorAlert('There was an error rejecting the bid.');
			});
	};

	return (
		<ConfirmModal
			visible
			type="warning"
			prompt="Are you sure you want to reject this bid? It will be hidden from your view."
			onAccept={() => {
				dispatchRejection();
			}}
			onCancel={onCancel}
			data-testid="reject-bid-modal"
		/>
	);
}

const generatePPADisplay = (bidData) => {
	return (
		<div className="">
			<KeyValPair
				val={formatters.percentage.formatter(bidData.annual_riser)}
				_key="Annual Riser"
				striped
			/>
			<KeyValPair
				val={bidData.om_included}
				_key="Operation & Management Expenses Included"
				striped
			/>
			<KeyValPair
				val={bidData.insurance_included}
				_key="Insurance Included"
				striped
			/>
			<KeyValPair val={bidData.buyout_options} _key="Buyout Options" striped />
			<KeyValPair
				val={bidData.conditions}
				_key="Notes and Conditions"
				striped
			/>
		</div>
	);
};

function BidDisplayBox({
	bidData,
	editable,
	selectable,
	showAdmin,
	onEdit,
	onSelect,
	onApply,
	onApprove,
	onReject,
	isApproved,
	isDeclined,
	isSelected,
	userIsAdmin,
	bidBy,
	hasApplied,
}) {
	const rateTypeDisplay = (rateType) => {
		const { options } = rateTypeSelect;
		const option = options.find((option) => option.value === rateType);
		if (!option) return rateType;
		return option.display;
	};

	let key_val_displayable_keys = Object.keys(bidData).filter(
		(key) =>
			!['rateType', 'id', 'rate', 'term'].includes(key) &&
			bidData[key] !== null &&
			key.indexOf('admin') === -1
	);
	let admin_keys = Object.keys(bidData).filter(
		(key) => key.indexOf('admin') !== -1
	);

	return (
		<div>
			{isSelected && (
				<div className="has-text-primary is-size-3 my-4">Selected Bid</div>
			)}
			<div className="card mt-2 mb-6">
				<div className="card-body">
					<div
						className={`${
							isSelected
							  ? 'has-background-success'
							  : isDeclined || isApproved === false
							  ? 'has-background-danger'
							  : 'has-background-primary'
						  }`}
					>
						<div className="card-header-title has-text-white">
							<div className="is-flex is-align-items-center is-justify-content-space-between is-fullwidth">
							<h3 className="is-size-5 has-text-white mr-6">
								{bidData.rate}
								{rateTypeDisplay(bidData.rate_type)} for {bidData.term} years
								{isSelected && ' - Bid Selected'}
								{isDeclined && !isSelected && ' (Declined by Applicant)'}
							</h3>
								<div className="buttons">
									{editable && (
										<button
											className="button is-primary is-inverted is-outlined mt-1 no-print no-render-print-view" // mt-1 basically just brings it to baseline
											onClick={onEdit}
										>
											Edit
										</button>
									)}
									{showAdmin && [null, undefined].includes(isApproved) && (
										<>
											<button
												className="button is-primary is-inverted is-outlined mt-1 no-print no-render-print-view" // mt-1 basically just brings it to baseline
												onClick={() => onApprove(true)}
											>
												Approve
											</button>
											<button
												className="button is-danger is-inverted is-outlined mt-1 no-print no-render-print-view" // mt-1 basically just brings it to baseline
												onClick={() => onApprove(false)}
											>
												Reject
											</button>
										</>
									)}
								{selectable && isApproved && !isSelected && (
									<>
										<button
											className="button is-primary is-inverted is-outlined mt-1 no-print no-render-print-view"
											onClick={onSelect}
										>
											Select This Bid
										</button>
										<button
											className="button is-danger is-inverted is-outlined mt-1 no-print no-render-print-view"
											onClick={onReject}
										>
											Reject
										</button>
									</>
								)}
								{isApproved && isSelected && hasApplied && (
									<span className="tag is-success is-medium mt-1">
										Applied
									</span>
								)}
								{isApproved && isSelected && onApply && !hasApplied && (
									<button
										className="button is-link is-inverted is-outlined mt-1 no-print no-render-print-view"
										onClick={onApply}
									>
										Apply
									</button>
								)}
								</div>
							</div>
						</div>
					</div>
					<div className="card-content">
						{isApproved === false && showAdmin && (
							<p className="pt-2 pb-5 has-text-danger">
								This bid has been rejected and will not display for the client.
								<a
									href="#"
									className="ml-3 has-text-grey-dark is-underlined"
									onClick={() => onApprove(true)}
								>
									Change to approval
								</a>
							</p>
						)}
						{isDeclined && showAdmin && (
							<p className="pt-2 pb-5 has-text-danger">
								This bid has been declined by the applicant and will not display for them.
								<a
									href="#"
									className="ml-3 has-text-grey-dark is-underlined"
									onClick={(e) => {
										e.preventDefault();
										onApprove(true);
									}}
								>
									Change to approval
								</a>
							</p>
						)}
						{generatePPADisplay(bidData)}
						{showAdmin && (
							<div className="my-2">
								{admin_keys.map((k) => {
									return (
										<KeyValPair
											key={k}
											val={bidData[k]}
											_key={startCase(k)}
											condensed
										/>
									);
								})}
							</div>
						)}
						{userIsAdmin && bidBy && (
							<div className="my-2">
								<KeyValPair
									data-testid="Bid From"
									val={
										[
											`${bidBy?.first_name || ''} ${bidBy?.last_name || ''}`.trim(),
											bidBy?.email,
											bidBy?.funder_name
										]
											.filter(Boolean)
											.join(', ') || 'N/A'
									}
									_key="Bid From"
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}

function BidDisplay(props) {
	const history = useHistory();
	const application_data = useSelector(
		(state) => state.applicationData?.app || []
	);
	const bids = useSelector((state) => state.applicationData?.bids || []);
	const bid_is_accepted = useSelector((state) =>
		bids
			.map((d) => d.id)
			.includes(state.applicationData?.app?.loan_estimate || -1)
	);
	const requester_permissions = useSelector(
		(state) => state.applicationData?.app?.requester_permissions || []
	)

	const [bidModalOpen, setBidModalOpen] = useState(false);
	const [editingBid, setEditingBid] = useState(null);
	const [approveRejectModalOpen, setApproveRejectModalOpen] = useState(false);
	const [acceptBidModalOpen, setAcceptBidModalOpen] = useState(false);
	const [rejectBidModalOpen, setRejectBidModalOpen] = useState(false);

	const user_is_admin = useSelector(state => state.user?.decodedTokenData?.admin);

	const user_id = useSelector(state => state.user?.decodedTokenData?.user_id || null);

	const APPLIED_STATUSES = ['APPLICATION_UNDERWAY', 'FUNDER_REVIEW', 'CLOSED', 'CLOSED_NOT_FUNDED'];
	const hasApplied = APPLIED_STATUSES.includes(application_data.status);

	const handleBidApply = (bid) => {
		console.log('[TEMPLATE DEBUG FE] handleBidApply called with bid:', { id: bid.id, bid_by: bid.bid_by, loan_product: bid.loan_product });
		toggleGlobalLoader(true);
		updateOrCreateApplication({
			id: application_data.id,
			loan_estimate: bid.id,
			status: 'APPLICATION_UNDERWAY',
		})
			.then((res) => {
				console.log('[TEMPLATE DEBUG FE] updateOrCreateApplication response:', { template_name: res?.template?.name, template: res?.template });
				return pullApplication(application_data.id);
			})
			.then(() => {
				const state = store.getState();
				console.log('[TEMPLATE DEBUG FE] After pullApplication, app template:', state.applicationData?.app?.template?.name);
				store.dispatch(actions.formFlow.setFormFlowCurrStep(1));
				prepareApplicationFormFlow();
				toggleGlobalLoader(false);
				history.push(`/apply/${application_data.id}`);
			})
			.catch(() => {
				toggleGlobalLoader(false);
				addErrorAlert('There was an error starting the application.');
			});
	};

	// Debug logging to help identify permission issues
	useEffect(() => {
		console.log('BidDisplay Debug Info:', {
			requester_permissions,
			user_id,
			user_is_admin,
			bids: bids.map(b => ({ 
				id: b.id, 
				is_approved: b.is_approved, 
				is_bid_decline: b.is_bid_decline,
				bid_by_id: b.bid_by?.id 
			}))
		});
	}, [requester_permissions, user_id, user_is_admin, bids]);

	return (
		<div className="bid-wrapper wrapper">
			<div className="my-3">
				{requester_permissions.includes('bid') && (
					<div className="level">
						<div className="level-left">
							<div className="level-item">
								<button
									className="button is-primary is-medium"
									onClick={() => setBidModalOpen(true)}
								>
									+ Submit a Bid
								</button>
							</div>
						</div>
					</div>
				)}

				<div className="columns is-multiline">
					<div className="column is-one-half">
						{bids
							.filter((bid) => {
								// System admins (CleanFi people) can see everything, including declined bids
								if (user_is_admin) {
									return true;
								}
								
								// For non-admins: exclude applicant-declined bids
								if (bid.is_bid_decline) {
									return false;
								}
								
								// Check if this specific bid was created by the current user
								const isMyBid = bid.bid_by?.id === user_id;
								
								// Bidders can see their own unapproved bids
								if (isMyBid) {
									return true;
								}
								
								// Everyone else (applicants, application admins): only see approved bids
								return bid.is_approved === true;
							})
							.sort((a, b) =>
								a.id === application_data.loan_estimate ? -1 : 1
							)
							.map((bid, idx) => (
								<BidDisplayBox
									key={bid.id}
									bidData={{ id: bid.id, ...bid.data }}
									editable={
										user_is_admin || // System admins can edit any bid
										(bid.bid_by?.id === user_id && !requester_permissions.includes('edit')) // Bidders can edit their own bids (unless they're also applicants)
									}
									selectable={
										!bid.is_bid_decline && ( // Don't show select/reject for declined bids
											(requester_permissions.includes('edit') && !bid_is_accepted) ||
											requester_permissions.includes('edit_requirements')
										)
									}
									isSelected={bid.id === application_data.loan_estimate}
									showAdmin={
										user_is_admin || 
										requester_permissions.includes('edit_requirements')
									}
								bidBy={bid.bid_by}
								onSelect={() => {
									setEditingBid({ id: bid.id, ...bid.data });
									setAcceptBidModalOpen(true);
								}}
								onEdit={() => {
									setEditingBid({ id: bid.id, ...bid.data });
									setBidModalOpen(true);
								}}
								onApply={
									(user_is_admin || requester_permissions.includes('edit'))
										? () => handleBidApply(bid)
										: null
								}
								onApprove={(is_approved) => {
									setEditingBid({ id: bid.id, ...bid.data, is_approved });
									setApproveRejectModalOpen(true);
								}}
								onReject={() => {
									setEditingBid({ id: bid.id, ...bid.data });
									setRejectBidModalOpen(true);
								}}
								isApproved={bid.is_approved}
								isDeclined={!!bid.is_bid_decline}
								userIsAdmin={user_is_admin}
								hasApplied={hasApplied}
								/>
							))}
					</div>
				</div>
			</div>

			{bidModalOpen && (
				<ApplicationBidModal
					productType="PPA"
					onCancel={() => {
						setBidModalOpen(false);
						setEditingBid(null);
					}}
					application_id={application_data.id}
					bidData={editingBid}
				/>
			)}

			{approveRejectModalOpen && (
				<ApproveOrRejectBidModal
					is_approved={editingBid.is_approved}
					application_id={application_data.id}
					bidData={editingBid}
					onCancel={() => {
						setApproveRejectModalOpen(false);
						setEditingBid(null);
					}}
				/>
			)}

			{acceptBidModalOpen && (
				<AcceptBidModal
					application_id={application_data.id}
					bidData={editingBid}
					onCancel={() => {
						setAcceptBidModalOpen(false);
						setEditingBid(null);
					}}
				/>
			)}

			{rejectBidModalOpen && (
				<RejectBidModal
					application_id={application_data.id}
					bidData={editingBid}
					onCancel={() => {
						setRejectBidModalOpen(false);
						setEditingBid(null);
					}}
				/>
			)}
		</div>
	);
}

export default BidDisplay;
